import type { ReactElement } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PercentileChip from "../PercentileChip";
import GreyNumeral from "../GreyNumeral";
import {
  colorRank,
  getPercentileFromRankOrPercentile,
  getRankPercentileText,
  nth,
} from "../utils";
import { COLUMN_GROUP_BORDER, REPORT_HOVER_CARD_SX } from "../constants";
import { partText, perChancePPPText } from "./utils";
import { TeamReportCellPart } from "./types";

/** A cell's hover breakdown: one line per part, label left and value right (with the part's own rank / percentile as
 * a grey numeral when the cell shows its own), then the rank / percentile line the cell's own hover would have
 * shown. */
function PartsCard({
  parts,
  decimals,
  footer,
  showRanks,
}: {
  parts: TeamReportCellPart[];
  decimals: number;
  footer?: string;
  showRanks: boolean;
}) {
  return (
    <Box component="span" className="CellParts" sx={{ display: "block" }}>
      {parts.map((part) => (
        <Box
          component="span"
          key={part.label}
          className="CellPart"
          sx={{ display: "flex", justifyContent: "space-between", columnGap: 1.5 }}
        >
          <span>{part.label}</span>
          <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {partText(part, decimals)}
            {showRanks && (
              <GreyNumeral>{getRankPercentileText(part.rank, part.percentile)}</GreyNumeral>
            )}
          </Box>
        </Box>
      ))}
      {footer && (
        <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
          {footer}
        </Box>
      )}
    </Box>
  );
}

export default function TeamReportDataTableCell({
  value,
  subValue,
  percentile,
  rank,
  rankPopulation,
  renderValue,
  isDisabled = false,
  isFrequency = false,
  groupStart = false,
  valueOnly = false,
  hybridNumeral = true,
  stripIndicator = false,
  lowSampleNote,
  parts,
  partDecimals = 1,
}: {
  value?: number | null;
  /** Composite-cell secondary number (e.g. a roll type's PPP riding its
   * rate cell) — rendered small and grey after the value */
  subValue?: number;
  percentile?: number;
  rank?: number;
  rankPopulation?: number;
  renderValue?: (value?: number) => React.ReactNode;
  isDisabled?: boolean;
  /** Tendency column — plain value + freq needle, never the quality ramp */
  isFrequency?: boolean;
  /** First column of a column group — draws a left divider */
  groupStart?: boolean;
  /** Column never carries a chip (e.g. Poss) — center the value, no chip */
  valueOnly?: boolean;
  /** Show the small rank/percentile numeral (anchor columns only — secondary
   * columns render the ramp-colored chip with the value alone) */
  hybridNumeral?: boolean;
  /** No effect since every frequency cell shows its grey number (Trevor,
   * 2026-10-10); the tables' `needleOrdinalColumns` that set it are left for a
   * cleanup pass */
  frequencyOrdinal?: boolean;
  /** Below a table's sample floor: the value prints, its chip / needle doesn't
   * (CONVENTIONS small-sample rule — grey is not zero) */
  stripIndicator?: boolean;
  /** Under this column's sample floor (e.g. "fewer than 15 3PA"): the value
   * prints grey with no chip, the note on hover */
  lowSampleNote?: string;
  /** Hover breakdown of the value (RV1: Position allowed / Finish allowed, …):
   * replaces the cell's own hover, whose line it repeats at the foot */
  parts?: TeamReportCellPart[];
  /** Decimals of the signed part values: the cell's own */
  partDecimals?: number;
}) {
  const numeralText = getRankPercentileText(rank, percentile);
  const resolvedPercentile = getPercentileFromRankOrPercentile(
    percentile,
    rank,
    rankPopulation
  );
  const hasIndicator =
    !isDisabled &&
    !stripIndicator &&
    !lowSampleNote &&
    (rank !== undefined || percentile !== undefined);
  const displayValue =
    isDisabled && value === 0
      ? "—"
      : renderValue
      ? renderValue(value ?? undefined)
      : value;

  // Cells that hide their numeral (secondary chips, needle columns) still
  // surface the rank/percentile in an unobtrusive hover tooltip.
  const hoverText =
    rank !== undefined && rank !== null
      ? `${nth(rank)}${rankPopulation ? ` of ${rankPopulation}` : " of 30"}`
      : percentile !== undefined && percentile !== null
      ? `${nth(Math.round(percentile))} percentile`
      : "";
  const hasParts = Boolean(parts?.length) && !isDisabled;
  // With a breakdown, its card is the cell's one hover (two tooltips on one value would stack)
  const hover = (title: string, child: ReactElement) =>
    hasParts ? (
      child
    ) : (
      <Tooltip title={title} enterDelay={300} placement="top" arrow>
        {child}
      </Tooltip>
    );
  const footer = lowSampleNote ?? (hasIndicator ? hoverText : "");

  const body = (
        lowSampleNote ? (
          hover(
            lowSampleNote,
            <Typography
              component="div"
              className="LowSampleValue"
              textAlign="center"
              color="text.disabled"
              sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
            >
              {displayValue}
            </Typography>
          )
        ) : !hasIndicator ? (
          <Typography
            component="div"
            textAlign="center"
            color={
              isDisabled
                ? "text.disabled"
                // Counts are context, not content (CONVENTIONS): chip-less
                // columns (Poss, FGA) render small and grey.
                : valueOnly
                ? "text.secondary"
                : "inherit"
            }
            sx={{ whiteSpace: "nowrap", fontSize: valueOnly ? "0.7rem" : "0.75rem" }}
          >
            {displayValue}
          </Typography>
        ) : isFrequency ? (
          // Tendency columns: the value + its rank / percentile as a small
          // grey number (no colour: a share is not good or bad).
          hover(
            hoverText,
            <Typography
              component="div"
              className="FrequencyPill"
              textAlign="center"
              sx={{ whiteSpace: "nowrap", fontSize: "0.862rem" }}
            >
              {displayValue}
              {subValue !== undefined && subValue !== null && (
                <Typography
                  component="span"
                  sx={{ fontSize: "0.782rem", color: "text.secondary", ml: "3px" }}
                >
                  {/* the composite's PPP (a roll type's, a drive direction's): per chance, floored at 0.00 */}
                  ({perChancePPPText(subValue, undefined, 1)})
                </Typography>
              )}
              <GreyNumeral>{numeralText}</GreyNumeral>
            </Typography>
          )
        ) : hybridNumeral ? (
          <PercentileChip
            className="QualityPill"
            value={displayValue}
            numeral={numeralText}
            percentile={resolvedPercentile}
            rank={colorRank(percentile, rank)}
            rankPopulation={rankPopulation}
          />
        ) : (
          // Secondary chips hide the numeral; hover reveals it.
          hover(
            hoverText,
            <PercentileChip
              className="QualityPill"
              value={displayValue}
              percentile={resolvedPercentile}
              rank={colorRank(percentile, rank)}
              rankPopulation={rankPopulation}
            />
          )
        )
  );

  return (
    <TableCell
      className="TeamReportDataTableCell"
      data-group-start={groupStart || undefined}
      sx={{ px: 0.5, py: 0.25, ...(groupStart && { borderLeft: COLUMN_GROUP_BORDER }) }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%" }}
      >
        {hasParts ? (
          <Tooltip
            disableInteractive
            placement="top"
            componentsProps={{ tooltip: { sx: REPORT_HOVER_CARD_SX } }}
            title={
              <PartsCard
                parts={parts as TeamReportCellPart[]}
                decimals={partDecimals}
                footer={footer || undefined}
                // under a sample floor the cell has no standing, so neither do its pieces
                showRanks={hasIndicator}
              />
            }
          >
            <Box
              className="CellPartsHover"
              sx={{ display: "inline-flex", alignItems: "center", cursor: "default" }}
            >
              {body}
            </Box>
          </Tooltip>
        ) : (
          body
        )}
      </Stack>
    </TableCell>
  );
}
